import { motion, useInView } from 'framer-motion';
import { ArrowDown, ArrowUp } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/tw-merge/cn';

import { OUTCOME_METRICS, WORKFLOW_STEPS } from '../data/personalized-data';

export default function ValueDeliverySection() {
  const sectionRef = useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef, { amount: 0.2 });
  const [activeStepNumber, setActiveStepNumber] = useState<string>('01');
  const [isWorkflowHovered, setIsWorkflowHovered] = useState(false);

  useEffect(() => {
    if (!isInView || isWorkflowHovered) return;

    const intervalId = window.setInterval(() => {
      setActiveStepNumber(currentStepNumber => {
        const currentIndex = WORKFLOW_STEPS.findIndex(
          step => step.number === currentStepNumber
        );
        const nextIndex = (currentIndex + 1) % WORKFLOW_STEPS.length;

        return WORKFLOW_STEPS[nextIndex].number;
      });
    }, 4000);

    return () => window.clearInterval(intervalId);
  }, [isInView, isWorkflowHovered]);

  // Custom sleek outline icons matching the screenshot's orange line and database styling
  const renderStepIcon = (iconType: string) => {
    switch (iconType) {
      case 'connect':
        return (
          <svg
            className="h-12 w-12 text-primary"
            viewBox="0 0 28 28"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <ellipse cx="14" cy="7" rx="8.5" ry="3.5" />
            <path d="M5.5 7v7c0 1.93 3.8 3.5 8.5 3.5s8.5-1.57 8.5-3.5V7" />
            <path d="M5.5 14v7c0 1.93 3.8 3.5 8.5 3.5s8.5-1.57 8.5-3.5v-7" />
          </svg>
        );
      case 'analyze':
        return (
          <svg
            className="h-9 w-9 text-primary"
            viewBox="0 0 32 32"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M6 6v20h20" />
            <path d="M26 12l-8 8-5-5-7 7" />
            <path d="M20 12h6v6" />
          </svg>
        );
      case 'recommend':
        return (
          <svg
            className="h-8 w-8 text-primary"
            viewBox="0 0 28 28"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m14 3-2.2 6.8a2 2 0 0 1-1.3 1.3L3.7 13.3l6.8 2.2a2 2 0 0 1 1.3 1.3L14 23.6l2.2-6.8a2 2 0 0 1 1.3-1.3l6.8-2.2-6.8-2.2a2 2 0 0 1-1.3-1.3L14 3z" />
          </svg>
        );
      case 'transform':
        return (
          <svg
            className="h-8 w-8 text-primary"
            viewBox="0 0 28 28"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 7l-4-4-4 4" />
            <path d="M17 3v13a4 4 0 0 1-4 4H4" />
            <path d="M7 25l4 4 4-4" />
            <path d="M11 29V16a4 4 0 0 1 4-4h9" />
          </svg>
        );
      default:
        return (
          <svg
            className="h-8 w-8 text-primary"
            viewBox="0 0 28 28"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <ellipse cx="14" cy="7" rx="8.5" ry="3.5" />
            <path d="M5.5 7v7c0 1.93 3.8 3.5 8.5 3.5s8.5-1.57 8.5-3.5V7" />
            <path d="M5.5 14v7c0 1.93 3.8 3.5 8.5 3.5s8.5-1.57 8.5-3.5v-7" />
          </svg>
        );
    }
  };

  return (
    <section
      ref={sectionRef}
      id="value-delivery-section"
      className="w-full bg-[#F7F4EF] py-20"
    >
      <div className="app-container">
        {/* Main Flex/Grid Layout */}
        <div className="flex flex-col items-center justify-between gap-10 lg:flex-row lg:items-center lg:gap-8">
          {/* Left Column: Heading, intro paragraph, and 3 vertically stacked bullet points */}
          <div className="flex w-full shrink-0 flex-col justify-center lg:w-[320px] xl:w-[340px]">
            <h2 className="mb-4 text-center font-fraunces text-2xl font-normal leading-[28px] text-foreground sm:text-left sm:text-[44px] sm:leading-[1.12]">
              How Chinobi
              <br />
              delivers value
            </h2>

            <p className="mb-9 text-xs leading-5 text-foreground-secondary sm:text-sm">
              Powered by Agentous AI and decades of operations expertise,
              Chinobi turns complex data into confident decisions — faster.
            </p>

            {/* 3 Vertically Stacked Pillars with Orange Bullets */}
            <div className="flex flex-col gap-4">
              {/* Bullet 1 */}
              <div>
                <div className="mb-1 flex items-center gap-2.5">
                  <span className="h-2 w-2 shrink-0 rounded-full bg-primary" />
                  <h4 className="text-sm font-semibold tracking-tight text-foreground">
                    Enterprise data, securely connected
                  </h4>
                </div>
                <p className="pl-[18px] text-[13px] leading-normal text-foreground-secondary">
                  Chinobi works with your systems — never your data.
                </p>
              </div>

              {/* Bullet 2 */}
              <div>
                <div className="mb-1 flex items-center gap-2.5">
                  <span className="h-2 w-2 shrink-0 rounded-full bg-primary" />
                  <h4 className="text-sm font-semibold tracking-tight text-foreground">
                    AI + human expertise
                  </h4>
                </div>
                <p className="pl-[18px] text-[13px] leading-normal text-foreground-secondary">
                  Advanced models paired with domain experts.
                </p>
              </div>

              {/* Bullet 3 */}
              <div>
                <div className="mb-1 flex items-center gap-2.5">
                  <span className="h-2 w-2 shrink-0 rounded-full bg-primary" />
                  <h4 className="text-sm font-semibold tracking-tight text-foreground">
                    Outcome-focused
                  </h4>
                </div>
                <p className="pl-[18px] text-[13px] leading-normal text-foreground-secondary">
                  Every recommendation measured against impact.
                </p>
              </div>
            </div>
          </div>

          {/* Center & Right Combined Interactive Stage */}
          <div className="relative flex w-full flex-col items-center justify-center gap-6 lg:flex-1 lg:flex-row lg:justify-end lg:gap-0">
            {/* Center: Workflow Container */}
            <div
              id="workflow-container"
              onMouseEnter={() => setIsWorkflowHovered(true)}
              onMouseLeave={() => setIsWorkflowHovered(false)}
              className="relative z-10 flex h-[376px] w-full max-w-[550px] select-none flex-col justify-between rounded-[28px] border border-[#FFFFFFB2] bg-[#262626] p-4 shadow-[0px_24p_60px_-28px_#28190C33] sm:p-6"
            >
              {WORKFLOW_STEPS.map(step => {
                const isActive = activeStepNumber === step.number;

                return (
                  <div
                    key={step.number}
                    id={`workflow-step-${step.number}`}
                    onClick={() => setActiveStepNumber(step.number)}
                    className={cn(
                      'relative flex cursor-pointer items-center rounded-[22px] transition-all duration-300',
                      isActive
                        ? 'z-20 h-[120px] p-4'
                        : 'hover:bg-white/[0.04] z-10 h-[58px] px-3 sm:px-4'
                    )}
                  >
                    {/* Animated Gliding Active Background */}
                    {isActive && (
                      <motion.div
                        layoutId="active-workflow-card-bg"
                        transition={{
                          type: 'spring',
                          stiffness: 380,
                          damping: 32,
                          mass: 0.7,
                        }}
                        className="pointer-events-none absolute inset-0 rounded-[22px] border border-[#FBEAE0] bg-[#FFF1EB] shadow-[0px_4px_30px_0px_#FF5B1A33]"
                      />
                    )}

                    {/* Step Card Inner Content */}
                    <div className="relative z-10 flex w-full items-start justify-between gap-3 sm:gap-4">
                      <div className="flex min-w-0 flex-1 items-start gap-3.5 sm:gap-4">
                        {/* Number Badge */}
                        <motion.span
                          layout="position"
                          transition={{
                            type: 'spring',
                            stiffness: 400,
                            damping: 30,
                          }}
                          className={cn(
                            'inline-flex shrink-0 items-center justify-center font-fraunces font-bold transition-colors duration-300',
                            isActive
                              ? 'h-20 w-10 rounded-[14px] bg-primary text-xs text-white'
                              : 'my-auto h-8 w-10 rounded-[10px] bg-white text-xs text-primary'
                          )}
                        >
                          {step.number}
                        </motion.span>

                        {/* Title & Description */}
                        <div className="flex min-w-0 flex-1 flex-col justify-center">
                          <motion.h4
                            layout="position"
                            className={cn(
                              'font-fraunces text-base font-normal leading-[28px] tracking-tight transition-colors duration-300 sm:text-lg',
                              isActive
                                ? 'mb-1.5 text-foreground'
                                : 'my-auto text-stroke-100'
                            )}
                          >
                            {step.title}
                          </motion.h4>

                          {isActive && (
                            <motion.p
                              initial={{ opacity: 0, y: 4 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.22, ease: 'easeOut' }}
                              className="max-w-[270px] text-[13px] leading-relaxed text-[#6B7280] sm:text-[13.5px]"
                            >
                              {step.description}
                            </motion.p>
                          )}
                        </div>
                      </div>

                      {/* Right Icon for Active State */}
                      {isActive && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.75, rotate: -6 }}
                          animate={{ opacity: 1, scale: 1, rotate: 0 }}
                          transition={{
                            type: 'spring',
                            stiffness: 400,
                            damping: 24,
                            delay: 0.04,
                          }}
                          className="shrink-0 pr-1 pt-1"
                        >
                          {renderStepIcon(step.iconType)}
                        </motion.div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Desktop Decorative Dashed Orange Connector Line with Center Node */}
            <div className="pointer-events-none relative z-0 hidden h-[340px] w-[72px] shrink-0 lg:block">
              {/* Bracket Dashed Line: Comes out from top & bottom of left card */}
              <svg className="h-full w-full" viewBox="0 0 72 340" fill="none">
                {/* Upper curve and horizontal bracket from left */}
                <path
                  d="M 0 35 H 36 V 146"
                  stroke="#FA3E00"
                  strokeWidth="1.75"
                  strokeDasharray="6 8"
                  fill="none"
                />
                <path
                  d="M 36 194 V 305 H 0"
                  stroke="#FA3E00"
                  strokeWidth="1.75"
                  strokeDasharray="6 10"
                  fill="none"
                />
                {/* Horizontal line extending from center to right card */}
                <path
                  d="M 56 170 H 72"
                  stroke="#FA3E00"
                  strokeWidth="1.75"
                  strokeDasharray="5 5"
                  fill="none"
                />
              </svg>

              {/* Glowing Node Dot in the center */}
              <div className="absolute left-[36px] top-1/2 h-6 w-6 -translate-x-1/2 -translate-y-1/2">
                <div className="absolute inset-0 rounded-full border-2 border-[#FF3E00] blur-[3px]" />
                <div className="absolute left-1/2 top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border border-[#FF3E00] bg-[#FF7040]" />
              </div>
            </div>

            {/* Right: Real Outcomes Container */}
            <div
              id="real-outcomes-container"
              className="relative z-10 flex h-full w-full max-w-[360px] flex-col justify-between rounded-[28px] border border-neutral-800/90 bg-[#232325] p-8 shadow-[] sm:p-6 lg:h-[376px]"
            >
              <h3 className="mb-8 font-fraunces text-[22px] font-semibold tracking-tight text-white">
                Real outcomes
              </h3>

              <div className="space-y-3">
                {OUTCOME_METRICS.map((metric, idx) => (
                  <div
                    key={idx}
                    className={cn(
                      'flex items-center gap-4',
                      idx !== OUTCOME_METRICS.length - 1 &&
                        'border-b border-stroke-100 pb-4'
                    )}
                  >
                    {/* Mint Green Direction Icon Circle */}
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#D8EFE4] text-[#115234]">
                      {metric.direction === 'up' ? (
                        <ArrowUp className="h-4 w-4 stroke-[2.5]" />
                      ) : (
                        <ArrowDown className="h-4 w-4 stroke-[2.5]" />
                      )}
                    </div>

                    {/* Metric details with bold serif numerical display */}
                    <div>
                      <div className="sm;leading-6 mb-1.5 font-fraunces text-3xl font-bold tracking-normal text-white">
                        {metric.value}
                      </div>
                      <div className="text-[13px] leading-snug text-stroke-100">
                        {metric.label}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
